"use client";

/**
 * ApplyTransitionOverlay — Signature-Moment der Landing.
 *
 * Wenn der Bewerber irgendwo auf der Landing "Bewerbung starten" klickt
 * (Sticky-Bar-CTA oder Hero-CTA), oeffnet sich ein Vollflaechen-Overlay,
 * in dem die SignatureFlip37-Coin abspielt: die grosse "37" dreht sich,
 * blitzt im Wendepunkt lime auf und enthuellt auf der Rückseite eine
 * Mini-Vorschau des Crew-Pass. Nach Abschluss (~1.15s Coin + ~0.45s
 * Hold/Slide-Echo = ~1.6s) leitet der Overlay automatisch auf /bewerben
 * weiter.
 *
 * Kommunikation:
 *   - Trigger: globales CustomEvent "37s:apply-start" am window-Object.
 *     Jede CTA-Quelle (Hero-CTA, StickyApplyBar) feuert das Event über
 *     den useApplyTransition()-Hook (siehe useApplyTransition.ts).
 *   - Eindeutiger Mount: dieser Overlay sitzt einmal in apps/web/src/app/page.tsx
 *     direkt unter <LenisProvider /> und lauscht permanent.
 *
 * prefers-reduced-motion:
 *   - SignatureFlip37 erkennt das selbst und überspringgt die Drehung —
 *     onComplete feuert sofort. Wir UEBERSPRINGEN dann das Overlay komplett
 *     und navigieren direkt: kein modaler Block, kein Schwarz-Flash.
 *
 * Mobile:
 *   - Die Coin skaliert über CSS-clamp. Auf schmalen Viewports begrenzen
 *     wir die size der Coin via min(80vw, 360px).
 *
 * Browser-Navigation:
 *   - Wir nutzen next/navigation router.push("/bewerben") für eine
 *     echte client-side Page-Transition (kein full reload). Funnel-Page
 *     wird während der Animation per router.prefetch vorgeladen.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import SignatureFlip37, {
  type SignatureFlip37Handle,
} from "../../bewerben/_components/SignatureFlip37";

/**
 * Globaler Event-Name. Wer den Funnel-Start triggern möchte, dispatcht:
 *   window.dispatchEvent(new CustomEvent(APPLY_START_EVENT))
 * Der useApplyTransition()-Hook unten kapselt das.
 */
export const APPLY_START_EVENT = "37s:apply-start";

const APPLY_URL = "/bewerben";

/**
 * Haengt die aktuelle URL-Query an /bewerben, damit UTM-/Prefill-/Lock-
 * Parameter (z. B. aus dem Link-Creator) NICHT verloren gehen, wenn der
 * Bewerber erst auf der Startseite landet und dann via CTA in den Funnel
 * geht (Gruender 2026-08-03: Ziel "Startseite -> Bewerben" soll die selbe
 * Attribution/Prefill tragen wie der Direktlink).
 */
function applyUrlWithSearch(): string {
  if (typeof window === "undefined") return APPLY_URL;
  const search = window.location.search;
  return search && search.length > 1 ? `${APPLY_URL}${search}` : APPLY_URL;
}
/** Coin-Spin (1.15s) + kurzer Echo-Hold + Slide-Out (~0.45s). */
const TOTAL_MS = 1600;
/** Hard-Deadline: selbst wenn weder onComplete noch der Slide-Timer feuern
 *  (z. B. weil SignatureFlip37 wegen prefers-reduced-motion-Edgecase oder
 *  einem Render-Race onComplete verschluckt), navigieren wir spätestens
 *  nach diesem Fenster. Verhindert "Klick ohne Wirkung". */
const HARD_DEADLINE_MS = 2200;

export function ApplyTransitionOverlay() {
  const router = useRouter();
  const pathname = usePathname();
  const [active, setActive] = useState(false);
  /** "slide" = Coin gleitet leicht nach rechts wie im
   *  landing_37_dreht_nach_rechts.html-Showpiece. */
  const [slide, setSlide] = useState(false);
  const flipRef = useRef<SignatureFlip37Handle | null>(null);
  const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Idempotenz-Guard: navigateToFunnel kann von mehreren Quellen aufgerufen
   *  werden (onComplete-Slide-Timer + Hard-Deadline). Nur einmal pushen. */
  const navigatedRef = useRef(false);

  // Funnel-Route vorladen, sobald die Landing montiert ist — die
  // tatsaechliche Navigation am Ende der Animation wird so spuerbar
  // schneller (Next prefetcht JS-Bundle und Server-Component-Payload).
  useEffect(() => {
    router.prefetch(APPLY_URL);
  }, [router]);

  // Reduced-Motion-Detect: wenn der Nutzer Animation reduziert hat,
  // überspringgen wir den Overlay komplett und navigieren direkt.
  // SignatureFlip37 könnte das auch, aber dann blitzt das schwarze
  // Modal kurz auf — schlechter UX als ein direkter Routenwechsel.
  const isReducedMotion = useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Zentrale Navigations-Funktion. Idempotent (navigatedRef), mit try/catch,
  // damit ein eventueller router.push-Fehler die Navigation nicht in einen
  // toten Zustand kippt - dann nehmen wir window.location als Fallback.
  // Danach wird das Overlay sofort wieder zugeklappt, damit auf der
  // Zielseite kein dunkler Schleier durchblitzt, falls die Landing noch
  // nicht unmounted ist (Next-Page-Transition haelt die alte Page kurz im
  // Speicher, bevor sie austauscht).
  const navigateToFunnel = useCallback(() => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    if (deadlineTimerRef.current) {
      clearTimeout(deadlineTimerRef.current);
      deadlineTimerRef.current = null;
    }
    if (navTimerRef.current) {
      clearTimeout(navTimerRef.current);
      navTimerRef.current = null;
    }
    const target = applyUrlWithSearch();
    try {
      router.push(target);
    } catch {
      if (typeof window !== "undefined") {
        window.location.assign(target);
      }
    }
    // Overlay-Lebensdauer wird NICHT mehr per setTimeout beendet (das war
    // die Flash-Quelle: 80ms reichten nicht für den Server-Component-
    // Fetch auf /bewerben - Landing blitzte auf bevor /bewerben da war).
    // Stattdessen prüfen wir unten per usePathname auf 'pathname ===
    // /bewerben' und kappen das Overlay erst dann.
  }, [router]);

  // Page-Transition-Detect: sobald /bewerben sichtbar ist, kann das
  // Overlay weg. Bis dahin BLEIBT es sichtbar - kein weisses Flackern,
  // kein erneutes Zeigen der Landing zwischen Spin und Funnel.
  useEffect(() => {
    if (!active) return;
    if (pathname && pathname.startsWith("/bewerben")) {
      // Kurz die Page rendern lassen (1 Frame), dann Overlay weg.
      const t = setTimeout(() => {
        setActive(false);
        setSlide(false);
        navigatedRef.current = false;
      }, 50);
      return () => clearTimeout(t);
    }
    return;
  }, [active, pathname]);

  // Listener auf das globale Event. Mehrfach-Trigger während laufender
  // Animation werden ignoriert (active-Guard).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      if (active) return;
      if (isReducedMotion()) {
        navigateToFunnel();
        return;
      }
      navigatedRef.current = false;
      setActive(true);
      // Hard-Deadline scharf schalten: selbst wenn SignatureFlip37s
      // onComplete-Callback aus irgendeinem Grund nicht feuert (Race,
      // unmount, Browser-Tab im Hintergrund mit gedrosselten Timern),
      // verlassen wir die Landing nach HARD_DEADLINE_MS.
      if (deadlineTimerRef.current) clearTimeout(deadlineTimerRef.current);
      deadlineTimerRef.current = setTimeout(() => {
        navigateToFunnel();
      }, HARD_DEADLINE_MS);
    };
    window.addEventListener(APPLY_START_EVENT, handler);
    return () => window.removeEventListener(APPLY_START_EVENT, handler);
  }, [active, isReducedMotion, navigateToFunnel]);

  // Body-Scroll während der Animation sperren — sonst kann der Nutzer
  // hinter dem Overlay scrollen und sieht die Landing-Bewegung durch
  // den (semitransparent gedachten) Hintergrund. Wir benutzen die
  // overflow-Sperre nur für den kurzen Moment, um keinen Layout-Shift
  // beim Reaktivieren der Page zu verursachen.
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);

  // Aufraeumen, falls die Komponente unmounted, während ein nav-Timer
  // noch läuft (z. B. Hot-Reload im Dev).
  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      if (deadlineTimerRef.current) clearTimeout(deadlineTimerRef.current);
    };
  }, []);

  // Wird gerufen, sobald SignatureFlip37 die Drehung beendet hat
  // (nach 1.15s) und im Idle-Floaty-Loop ist. Wir starten den
  // Coin-Slide-Out und navigieren danach.
  const handleSpinComplete = useCallback(() => {
    setSlide(true);
    navTimerRef.current = setTimeout(() => {
      navigateToFunnel();
    }, TOTAL_MS - 1150);
  }, [navigateToFunnel]);

  if (!active) return null;

  return (
    <div className="apply-overlay" role="dialog" aria-modal="true" aria-label="Bewerbung wird gestartet">
      <div className="apply-overlay-stage" data-slide={slide ? "true" : "false"}>
        <SignatureFlip37
          ref={flipRef}
          autoStart
          onComplete={handleSpinComplete}
          // Coin-Bühne klein genug für Mobile (80vw cap), gross genug
          // für Desktop (max 360px).
          size={typeof window !== "undefined" ? Math.min(window.innerWidth * 0.8, 360) : 360}
          crewPassPreview={<MiniCrewPass />}
        />
        <p className="apply-overlay-lead" aria-live="polite">
          Dein Crew Pass wird vorbereitet.
        </p>
      </div>

      <style jsx>{`
        /* Light-Default: heller Hintergrund mit dezenter Brand-Atmosphaere -
           kein Schwarz-Flash beim Klick, kein Theme-Bruch zwischen Landing
           (light) und /bewerben (light). Der dunkle Hintergrund wird nur
           im Dark-Mode (data-theme=dark auf <html>) angezogen. */
        .apply-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background:
            radial-gradient(1100px 640px at 50% 30%, rgba(19, 178, 101, 0.12), transparent 60%),
            radial-gradient(900px 560px at 50% 80%, rgba(31, 122, 78, 0.10), transparent 72%),
            var(--bg, #f6faf7);
          /* Sanftes Einblenden, damit der Modal nicht hart aufpoppt. */
          animation: apply-fadein 220ms ease-out;
        }
        /* Dark-Mode: explizit den dunklen Showpiece-Look ziehen. Wir benutzen
           bewusst NICHT data-theme=dark hartcodiert auf dem Overlay - das
           Overlay vererbt das Theme vom <html>-Root, das das No-FOUC-Skript
           aus dem Cookie "37s_theme" gesetzt hat. So bleibt Light-Mode-User
           im Light-Mode und Dark-Mode-User im Dark-Mode. */
        :root[data-theme='dark'] .apply-overlay {
          background:
            radial-gradient(1100px 640px at 50% 30%, rgba(19, 178, 101, 0.22), transparent 60%),
            radial-gradient(900px 560px at 50% 80%, rgba(8, 40, 22, 0.55), transparent 72%),
            #04100a;
        }

        .apply-overlay-stage {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 32px;
          transition:
            transform 450ms cubic-bezier(0.6, 0, 0.2, 1),
            opacity 380ms ease-out;
        }
        /* Coin gleitet nach rechts UND faded leicht aus -> Page-Transition. */
        .apply-overlay-stage[data-slide="true"] {
          transform: translateX(60px) scale(0.96);
          opacity: 0;
        }

        .apply-overlay-lead {
          margin: 0;
          font-family: var(--font-text, system-ui, sans-serif);
          font-size: 14px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          /* Theme-aware: im Light-Mode dunkler Subtext, im Dark-Mode heller.
             var(--fg-muted) fällt mit Fallback auf den bisherigen Hell-Wert
             zurück, falls das Token in einem Sub-Tree nicht aufgeloest wird. */
          color: var(--fg-muted, rgba(234, 244, 236, 0.7));
          text-align: center;
        }
        :root[data-theme='dark'] .apply-overlay-lead {
          color: rgba(234, 244, 236, 0.7);
        }

        @keyframes apply-fadein {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @media (max-width: 540px) {
          .apply-overlay-lead {
            font-size: 12px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .apply-overlay,
          .apply-overlay-stage {
            animation: none;
            transition: none;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * MiniCrewPass — die Mini-Vorschau der Crew-Pass-Karte für die
 * Coin-Rückseite. Inspiration aus docs/prototypes/landing_37_dreht_nach_rechts.html
 * (.pass-Block, 340px breit). Wir nutzen NICHT den voll funktionalen
 * CrewPass-Stream (der hängt an FunnelState), sondern eine statisch
 * gerenderte Teaser-Karte — Bewerber soll nur einen Vorgeschmack auf
 * "das wird Deine Akte" bekommen.
 */
function MiniCrewPass() {
  return (
    <div className="mini-pass" aria-hidden="true">
      <div className="mini-pass-header">
        <div className="mini-pass-brand">
          CREW PASS
          <small>Priority Boarding</small>
        </div>
        <div className="mini-pass-coin" aria-hidden="true">
          {/* Coin-Look v2 (User-Wunsch 10.06.2026 v5): Premium-Coin mit
              gefuelltem grünem Kern + duennem Lime-Akzent + größerer 37. */}
          <svg viewBox="0 0 48 48" width="48" height="48">
            <defs>
              <radialGradient id="mp-coin-fill" cx="50%" cy="40%" r="60%">
                <stop offset="0%" stopColor="#1f7a4e" />
                <stop offset="100%" stopColor="#063A22" />
              </radialGradient>
            </defs>
            <circle cx="24" cy="24" r="21" fill="url(#mp-coin-fill)"
                    stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
            {/* Lime-Akzent-Bogen oben, duenn 2px wie auf dem grossen Coin. */}
            <path d="M24 4 A20 20 0 0 1 40 14"
                  fill="none" stroke="#C8F542"
                  strokeWidth="2" strokeLinecap="round" />
            <text x="24" y="24" textAnchor="middle"
                  dominantBaseline="central"
                  fill="#FFFFFF" fontWeight="700" fontSize="16"
                  fontFamily="var(--font-brand, system-ui)"
                  style={{ letterSpacing: '-0.02em' }}>37</text>
          </svg>
        </div>
      </div>
      <div className="mini-pass-strip">
        <span>First Class Professional GmbH</span>
      </div>
      <div className="mini-pass-grid">
        <div className="mini-pass-field">
          <div className="mini-pass-k">Crew-Mitglied</div>
          <div className="mini-pass-v mini-pass-v--empty">Du</div>
        </div>
        <div className="mini-pass-field">
          <div className="mini-pass-k">Pass-Nr.</div>
          <div className="mini-pass-v mini-pass-v--mono">FCP-•••</div>
        </div>
        <div className="mini-pass-field">
          <div className="mini-pass-k">Rolle</div>
          <div className="mini-pass-v mini-pass-v--empty">-</div>
        </div>
        <div className="mini-pass-field">
          <div className="mini-pass-k">Standort</div>
          <div className="mini-pass-v mini-pass-v--empty">-</div>
        </div>
      </div>
      <div className="mini-pass-perf" aria-hidden="true">
        <span />
      </div>

      <style jsx>{`
        .mini-pass {
          position: relative;
          width: 300px;
          max-width: 80vw;
          border-radius: 18px;
          overflow: hidden;
          background: #0c1a12;
          color: #eaf4ec;
          box-shadow:
            0 36px 74px rgba(0, 0, 0, 0.55),
            0 2px 0 rgba(255, 255, 255, 0.12) inset;
        }
        .mini-pass-header {
          background: linear-gradient(135deg, #0a6b3c, #063a22);
          color: #fff;
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .mini-pass-brand {
          font-family: var(--font-brand, system-ui, sans-serif);
          font-weight: 700;
          font-size: 17px;
          line-height: 1.1;
          letter-spacing: 0.01em;
        }
        .mini-pass-brand small {
          display: block;
          font-size: 8px;
          letter-spacing: 0.24em;
          color: rgba(255, 255, 255, 0.55);
          font-weight: 600;
          font-family: var(--font-text, system-ui, sans-serif);
          margin-top: 4px;
          text-transform: uppercase;
        }
        .mini-pass-coin {
          width: 48px;
          height: 48px;
          line-height: 0;
          filter: drop-shadow(0 6px 12px rgba(0,0,0,0.35));
        }
        .mini-pass-strip {
          font-family: var(--font-brand, system-ui, sans-serif);
          font-weight: 600;
          font-size: 9.5px;
          letter-spacing: 0.18em;
          padding: 6px 16px;
          text-transform: uppercase;
          display: flex;
          justify-content: flex-start;
          color: rgba(234, 244, 236, 0.72);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }
        .mini-pass-grid {
          padding: 12px 16px 8px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px 10px;
        }
        .mini-pass-k {
          font-size: 7.5px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(234, 244, 236, 0.5);
          font-weight: 600;
          margin-bottom: 2px;
        }
        .mini-pass-v {
          font-family: var(--font-brand, system-ui, sans-serif);
          font-weight: 600;
          font-size: 12px;
        }
        .mini-pass-v--mono {
          font-family: var(--font-mono, "IBM Plex Mono", monospace);
          font-size: 11px;
          color: #13b265;
        }
        .mini-pass-v--empty {
          opacity: 0.45;
          font-weight: 500;
        }
        .mini-pass-perf {
          position: relative;
          height: 14px;
        }
        .mini-pass-perf span {
          position: absolute;
          top: 50%;
          left: 12px;
          right: 12px;
          border-top: 1.5px dashed rgba(255, 255, 255, 0.18);
        }
      `}</style>
    </div>
  );
}
